// MoveUnitsDialog — moves hardware units between stores and "Unassigned"
// (TD-671 Task 17, operator rulings R3 and R5).
//
// Opened from the Move button of a one-time row (manage-stores members only;
// ProductsTab decides). The dialog loads the live stores itself when it opens:
// nothing on the Products surface loads them any more (Task 8 retired the
// per-row store select), and they are needed for the To options and to name the
// From buckets. The From options are the item's own buckets with units > 0
// (Unassigned first, then each assigned store); the To options are every live
// store plus Unassigned, minus whatever From is, plus "+ Create new store…",
// which swaps the body for StoreForm and returns to the form with the new store
// selected (today's create-then-assign capability, kept).
//
// Idempotency (F1, client half). One Idempotency-Key per user submit attempt:
// a submit whose (from, to, quantity) matches the previous attempt REUSES that
// attempt's key, so a retry after a network error or a 409 concurrent-commit
// reaches the server's replay protection instead of moving twice. Editing any of
// the three fields, or reopening the dialog (the form remounts), takes a fresh
// key. A definitive refusal (any other 4xx) also drops the key: the server has
// answered for it, so the next attempt is a new attempt.
//
// Rule 59: success hands the response item to the owner (onMoved), which
// re-seeds the row from it; the dialog never guesses the new split. A 422
// INSUFFICIENT_QUANTITY is mapped by CODE to client copy (the server's wording
// stays unchanged on the wire) and asks the owner to refetch (onStale) because
// the buckets this dialog offered were stale; the From options recompute from the
// refreshed item. A 404 (the product or a store is gone) reloads the stores AND
// asks the owner to refetch, with its own copy.
//
// While a POST is in flight the dialog cannot be closed (closeDisabled): a later
// uncertain failure would otherwise be recorded after close() had already looked,
// and the owner would never be told to refetch.
//
// Inline styles only — no Tailwind. Every button is type="button" / "submit".

import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  useEmbedTokens,
  EmbedBanner,
  EmbedButton,
  EmbedDialog,
  EmbedField,
  EmbedInput,
  EmbedSelect,
  EmbedText,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import { idempotencyHeaders, idempotencyUserMessage, IDEMPOTENCY_ERROR_CODES } from "../../embed-shared/idempotency-errors";
import { moveOwnedProductUnits, type OwnedItemWire } from "./owned-products-api";
import { listStores, type CustomerStoreWire } from "./store-api";
import { StoreForm } from "./StoreForm";

/** The unassigned bucket in the selects (a store id is a UUID, never this). */
const UNASSIGNED = "unassigned";
const CREATE_STORE = "__create_store__";

export interface MoveUnitsDialogProps {
  /** The one-time item to move units of; null keeps the dialog closed. */
  item: OwnedItemWire | null;
  onClose: () => void;
  /** The element focus returns to on close (the row's Move button). */
  returnFocusTo?: HTMLElement | null;
  /** Called with the server's item after a successful move (rule 59). */
  onMoved: (item: OwnedItemWire) => void;
  /** The buckets were stale (422 INSUFFICIENT_QUANTITY): refetch the list. */
  onStale: () => void;
  /** The dialog closed after a failure that may or may not have committed (a
   *  network error, a 5xx, a 409 concurrent-commit): refetch, so reopening never
   *  offers pre-move buckets under a fresh key. */
  onMaybeStale?: () => void;
}

interface Bucket {
  /** UNASSIGNED or a store id. */
  value: string;
  label: string;
  units: number;
}

function bucketsOf(item: OwnedItemWire, stores: CustomerStoreWire[]): Bucket[] {
  const names = new Map(stores.map((s) => [s.id, s.name]));
  const out: Bucket[] = [];
  const unassigned = item.unassigned_quantity ?? 0;
  if (unassigned > 0) out.push({ value: UNASSIGNED, label: "Unassigned", units: unassigned });
  for (const a of item.assignments ?? []) {
    if (a.quantity > 0) out.push({ value: a.store.id, label: names.get(a.store.id) ?? a.store.name, units: a.quantity });
  }
  return out;
}

/** Client copy for a refused move (final review A4/B1). The 422
 *  INSUFFICIENT_QUANTITY message and the 404 text stay as the server sends them
 *  (API consumers and the migration-181 deploy marker depend on them); the embed
 *  never shows either raw. */
export const MOVE_INSUFFICIENT_MESSAGE = "There aren't enough units there to move that many.";
export const MOVE_GONE_MESSAGE = "That store or product is no longer available. The options have been refreshed.";

function moveErrorMessage(err: ApiError): string {
  if (err.code === "INSUFFICIENT_QUANTITY") return MOVE_INSUFFICIENT_MESSAGE;
  if (err.status === 404) return MOVE_GONE_MESSAGE;
  return idempotencyUserMessage(err.code) ?? err.message;
}

const toWireId = (value: string): string | null => (value === UNASSIGNED ? null : value);

function MoveForm({
  item,
  onClose,
  onMoved,
  onStale,
  onUncertain,
  submitting,
  onSubmittingChange,
}: Pick<MoveUnitsDialogProps, "onClose" | "onMoved" | "onStale"> & {
  item: OwnedItemWire;
  onUncertain: (uncertain: boolean) => void;
  /** A move POST is in flight; owned by MoveUnitsDialog so it can block closing. */
  submitting: boolean;
  onSubmittingChange: (submitting: boolean) => void;
}) {
  const t = useEmbedTokens();
  const productId = item.product.id;

  const [stores, setStores] = useState<CustomerStoreWire[] | null>(null);
  const [storesError, setStoresError] = useState<string | null>(null);
  const loadStores = useCallback(() => {
    setStoresError(null);
    listStores().then(
      (list) => setStores(list),
      (err: unknown) => setStoresError(err instanceof ApiError ? err.message : "Failed to load your stores"),
    );
  }, []);
  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const buckets = useMemo(() => bucketsOf(item, stores ?? []), [item, stores]);
  const [fromChoice, setFromChoice] = useState<string>("");
  const [toChoice, setToChoice] = useState<string>("");
  const [quantity, setQuantity] = useState("1");
  const [creating, setCreating] = useState(false);
  // Where focus goes when the form comes back from StoreForm: the To select after
  // Cancel, the submit button after a save (the next step).
  const formRef = useRef<HTMLFormElement>(null);
  const returnFocus = useRef<"to" | "submit" | null>(null);
  useEffect(() => {
    if (creating || !returnFocus.current) return;
    const target = returnFocus.current === "to" ? `[id="move-to-${productId}"]` : 'button[type="submit"]';
    returnFocus.current = null;
    formRef.current?.querySelector<HTMLElement>(target)?.focus();
  }, [creating, productId]);
  const [error, setError] = useState<string | null>(null);

  // The From in force: the customer's pick while that bucket still exists, else
  // the first bucket (a refetch after a stale 422 can retire the picked one).
  const from = buckets.find((b) => b.value === fromChoice) ?? buckets[0];
  // A recompute that lands on a different From bucket (a refetch retired the
  // picked one) restarts from one unit: the old quantity was sized for another bucket.
  const fromValue = from?.value;
  useEffect(() => {
    setQuantity("1");
  }, [fromValue]);
  const toOptions = useMemo(
    () => [
      ...(from?.value !== UNASSIGNED ? [{ value: UNASSIGNED, label: "Unassigned" }] : []),
      ...(stores ?? []).filter((s) => s.id !== from?.value).map((s) => ({ value: s.id, label: s.name })),
    ],
    [stores, from],
  );
  // A To equal to From is no move; drop a stale pick instead of submitting it.
  const to = toChoice === CREATE_STORE || toOptions.some((o) => o.value === toChoice) ? toChoice : "";

  // One key per submit attempt (see the header): reused only while the attempt's
  // signature is unchanged.
  const attempt = useRef<{ sig: string; key: string } | null>(null);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!from || !to || to === CREATE_STORE) return;
      const n = Number(quantity);
      if (quantity.trim() === "" || !Number.isInteger(n) || n < 1 || n > from.units) {
        setError(`Enter a quantity between 1 and ${from.units}.`);
        return;
      }
      const sig = `${from.value}|${to}|${n}`;
      if (attempt.current?.sig !== sig) {
        attempt.current = { sig, key: idempotencyHeaders()["Idempotency-Key"] };
      }
      onSubmittingChange(true);
      setError(null);
      try {
        const updated = await moveOwnedProductUnits(
          productId,
          { from_store_id: toWireId(from.value), to_store_id: toWireId(to), quantity: n },
          attempt.current.key,
        );
        onUncertain(false);
        onMoved(updated);
        // The POST is over: clear the flag BEFORE closing, or close()'s in-flight
        // backstop would report a successful move as uncertain.
        onSubmittingChange(false);
        onClose();
      } catch (err) {
        if (err instanceof ApiError) {
          // The idempotency family and the two move-specific refusals have their
          // own copy; anything else shows the server's text. The server's 422
          // wording ("...in the source") names a concept the customer never sees,
          // so it is mapped by CODE here and left unchanged on the wire.
          setError(moveErrorMessage(err));
          // A definitive refusal ends the attempt; a concurrent-commit 409 and a
          // network/5xx failure are exactly what a same-key retry is for.
          if (err.status >= 400 && err.status < 500 && err.code !== IDEMPOTENCY_ERROR_CODES.CONCURRENT_COMMIT) {
            attempt.current = null;
          } else {
            onUncertain(true);
          }
          if (err.code === "INSUFFICIENT_QUANTITY") onStale();
          if (err.status === 404) {
            // The product or a store (either side) is gone: the buckets AND the
            // store list this dialog offers are both out of date.
            loadStores();
            onStale();
          }
        } else {
          onUncertain(true);
          setError("Failed to move units. Please try again.");
        }
      } finally {
        onSubmittingChange(false);
      }
    },
    [from, to, quantity, productId, onMoved, onClose, onStale, onUncertain, onSubmittingChange, loadStores],
  );

  if (creating) {
    return (
      <StoreForm
        showHeading={false}
        onBack={() => {
          returnFocus.current = "to";
          setCreating(false);
        }}
        onSaved={(store) => {
          setStores((prev) => [...(prev ?? []), store]);
          setToChoice(store.id);
          returnFocus.current = "submit";
          setCreating(false);
        }}
      />
    );
  }

  if (storesError) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
        <EmbedBanner tone="error">{storesError}</EmbedBanner>
        <div style={{ display: "flex", gap: t.spacing.sm, justifyContent: "flex-end" }}>
          <EmbedButton type="button" variant="secondary" onClick={onClose}>
            Cancel
          </EmbedButton>
          <EmbedButton type="button" variant="primary" onClick={loadStores}>
            Retry
          </EmbedButton>
        </div>
      </div>
    );
  }

  if (stores === null) {
    return (
      <div role="status">
        <EmbedText tone="secondary" size="sm">
          Loading your stores…
        </EmbedText>
      </div>
    );
  }

  if (!from) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
        {error && <EmbedBanner tone="error">{error}</EmbedBanner>}
        <EmbedText tone="secondary" size="sm">
          There are no units to move.
        </EmbedText>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <EmbedButton type="button" variant="secondary" onClick={onClose}>
            Close
          </EmbedButton>
        </div>
      </div>
    );
  }

  const id = (name: string) => `move-${name}-${productId}`;
  return (
    <form ref={formRef} onSubmit={handleSubmit} noValidate>
      <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.xs }}>
        {error && <EmbedBanner tone="error">{error}</EmbedBanner>}
        <EmbedField label="From" htmlFor={id("from")}>
          <EmbedSelect
            id={id("from")}
            value={from.value}
            disabled={submitting}
            onChange={(e) => {
              setFromChoice(e.target.value);
              setQuantity("1");
              setError(null);
            }}
          >
            {buckets.map((b) => (
              <option key={b.value} value={b.value}>{`${b.label} · ${b.units}`}</option>
            ))}
          </EmbedSelect>
        </EmbedField>
        <EmbedField label="To" htmlFor={id("to")}>
          <EmbedSelect
            id={id("to")}
            value={to}
            disabled={submitting}
            onChange={(e) => {
              setError(null);
              if (e.target.value === CREATE_STORE) {
                setCreating(true);
                return;
              }
              setToChoice(e.target.value);
            }}
          >
            <option value="" disabled>
              Choose a destination
            </option>
            {toOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            <option value={CREATE_STORE}>+ Create new store…</option>
          </EmbedSelect>
        </EmbedField>
        <EmbedField label="Quantity" htmlFor={id("quantity")}>
          <EmbedInput
            id={id("quantity")}
            type="number"
            inputMode="numeric"
            min={1}
            max={from.units}
            step={1}
            value={quantity}
            disabled={submitting}
            onChange={(e) => {
              setQuantity(e.target.value);
              setError(null);
            }}
          />
        </EmbedField>
      </div>
      <div style={{ display: "flex", gap: t.spacing.sm, justifyContent: "flex-end", marginTop: t.spacing.md, flexWrap: "wrap" }}>
        <EmbedButton type="button" variant="secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </EmbedButton>
        <EmbedButton type="submit" variant="primary" loading={submitting} disabled={submitting || to === ""}>
          Move units
        </EmbedButton>
      </div>
    </form>
  );
}

export function MoveUnitsDialog({ item, onClose, returnFocusTo = null, onMoved, onStale, onMaybeStale }: MoveUnitsDialogProps) {
  // Set by a failed submit that may have committed; reported once, on close.
  const uncertain = useRef(false);
  const setUncertain = useCallback((v: boolean) => {
    uncertain.current = v;
  }, []);
  // A POST is in flight. Lifted out of MoveForm so the dialog itself can refuse
  // to close meanwhile (closeDisabled, like the cancel dialog): closing mid-POST
  // let a later network/5xx/409 failure set `uncertain` AFTER close() had already
  // checked it, so the owner was never told to refetch and the customer could
  // move the same units twice. The ref is the synchronous truth close() reads;
  // the state drives closeDisabled.
  const submittingRef = useRef(false);
  const [submitting, setSubmittingState] = useState(false);
  const setSubmitting = useCallback((v: boolean) => {
    submittingRef.current = v;
    setSubmittingState(v);
  }, []);
  const close = useCallback(() => {
    // Backstop: a close that still gets through mid-POST (the owner dropping the
    // item) has an unknown outcome, exactly like a failure that may have committed.
    if (uncertain.current || submittingRef.current) {
      uncertain.current = false;
      onMaybeStale?.();
    }
    onClose();
  }, [onClose, onMaybeStale]);
  return (
    <EmbedDialog
      open={item != null}
      onClose={close}
      title={item ? `Move ${item.product.name}` : "Move units"}
      size="small"
      returnFocusTo={returnFocusTo}
      closeDisabled={submitting}
    >
      {item && (
        <MoveForm
          key={item.product.id}
          item={item}
          onClose={close}
          onMoved={onMoved}
          onStale={onStale}
          onUncertain={setUncertain}
          submitting={submitting}
          onSubmittingChange={setSubmitting}
        />
      )}
    </EmbedDialog>
  );
}
